import TextSingleLine from 'Survey/components/question-types/TextSingleLine'
import MultipleChoice from 'Survey/components/question-types/MultipleChoice'
import MultipleSelect from 'Survey/components/question-types/MultipleSelect'
import Date from 'Survey/components/question-types/Date'
import File from 'Survey/components/question-types/File'
import Checkbox from 'Survey/components/question-types/Checkbox'
import Score from 'Survey/components/question-types/Score'
import PhoneNumber from 'Survey/components/question-types/PhoneNumber'
import OptInRadio from 'Survey/components/question-types/OptInRadio'
import { useAct } from 'DashboardUser/store'
import restClient from 'Shared/hooks/restClient'
import './DashboardField.scoped.scss'

const questionTypeComponents = {
  'text': TextSingleLine,
  'multiple_choice': MultipleChoice, // radios
  'multiple_select': MultipleSelect, // checkboxes
  'checkbox': Checkbox,
  'score': Score,
  'ranking': Score,
  'name': TextSingleLine,
  'email': TextSingleLine,
  'postcode': TextSingleLine,
  'phone_number': PhoneNumber,
  'instruction': () => <div />,
  'opt_in_radio': OptInRadio,
  'date': Date,
  'file': File,
  'unknown': () => <div>Unknown</div>,
}

const colorMap = {
  goal: {
    'Completed': 'green-background',
    'Not Completed': 'red-background'
  },
  completable_goal: {
    'Completed': 'green-background',
    'Not Completed': 'red-background'
  },
  search: {
    'Yes': 'green-background',
    'No': 'red-background'
  }
}

const actionableCellConfig = {
  resubscription: {
    apiRoute: (dashboardTargetId) => `/api/user/dashboard_targets/${dashboardTargetId}/resubscribe`,
    activeParamKey: 'unsubscribe',
    activeValue: 'Subscribed',
    inactiveValue: 'Unsubscribed',
    activeButtonLabel: 'Resubscribe',
    undoButtonLabel: 'Undo',
    getActivePayload: (fieldId) => ({ dashboardFieldId: fieldId, unsubscribe: false }),
    getInactivePayload: (fieldId) => ({ dashboardFieldId: fieldId, unsubscribe: true }),
    isActive: (value) => value !== 'Unsubscribed'
  },
  completable_goal: {
    apiRoute: (dashboardTargetId) => `/api/user/dashboard_targets/${dashboardTargetId}/complete_goal`,
    activeParamKey: 'completed',
    activeValue: 'Completed',
    inactiveValue: 'Not Completed',
    activeButtonLabel: 'Complete',
    undoButtonLabel: 'Undo',
    getActivePayload: (fieldId) => ({ dashboardFieldId: fieldId, completed: true }),
    getInactivePayload: (fieldId) => ({ dashboardFieldId: fieldId, completed: false }),
    isActive: (value) => value === 'Completed'
  }
}

export default function DashboardField ({ field, dashboardTarget, className = '', onBackgroundColorChange = () => {} }) {
  if (field.source === 'answerable_question') {
    return <AnswerCell
      field={field}
      dashboardTarget={dashboardTarget}
      className={className}
      onBackgroundColorChange={onBackgroundColorChange}
    />
  }

  const value = dashboardTarget[field.id]

  useEffect(() => {
    if (field.config?.colorCodeBoolean && onBackgroundColorChange) {
      const backgroundColor = colorMap[field.source]?.[value] || '';
      onBackgroundColorChange(backgroundColor)
    }
  }, [value])

  if (field.source === 'resubscription' || field.source === 'completable_goal') {
    return <ActionableCell 
      value={value} 
      field={field} 
      dashboardTarget={dashboardTarget} 
      className={className} 
      onBackgroundColorChange={onBackgroundColorChange}
    />
  }

  return <span className={className}>{ value }</span>;
}

function ActionableCell({ value, field, dashboardTarget, className = '', onBackgroundColorChange = () => {} }) {
  const config = actionableCellConfig[field.source]

  const [undoable, setUndoable] = useState(false)
  const [isActive, setIsActive] = useState(config.isActive(value))
  const undoTimeoutRef = useRef(null)
  const undoTimeoutMs = 10000 // 10 seconds

  useEffect(() => {
    if (field.config?.colorCodeBoolean && onBackgroundColorChange) {
      const currentValue = isActive ? config.activeValue : config.inactiveValue
      const backgroundColor = colorMap[field.source]?.[currentValue] || '';
      onBackgroundColorChange(backgroundColor)
    }
  }, [isActive, field.config?.colorCodeBoolean, onBackgroundColorChange])

  const clearUndoTimeout = () => {
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current)
      undoTimeoutRef.current = null
    }
  }

  useEffect(() => {
    if (!undoable || !isActive) {
      return
    }

    clearUndoTimeout()
    undoTimeoutRef.current = setTimeout(() => setUndoable(false), undoTimeoutMs)
    return clearUndoTimeout
  }, [undoable, isActive])

  const activate = () => {
    restClient.post(config.apiRoute(dashboardTarget.id), config.getActivePayload(field.id))
      .then(() => {
        setUndoable(true)
        setIsActive(true)
      })
  }

  const deactivate = () => {
    restClient.post(config.apiRoute(dashboardTarget.id), config.getInactivePayload(field.id))
      .then(() => {
        clearUndoTimeout()
        setUndoable(false)
        setIsActive(false)
      })
  }

  const button = useMemo(() => {
    if (!isActive) {
      return <button className="button secondary smallest" onClick={activate}>{config.activeButtonLabel}</button>
    }
    if (undoable) {
      return <button className="button secondary smallest" onClick={deactivate}>{config.undoButtonLabel}</button>
    }
    return null;
  }, [isActive, undoable]);

  return <div className="actionable-cell">
    <span className={className}>{ isActive ? config.activeValue : config.inactiveValue }</span>
    {button}
  </div>
}

function AnswerCell({ field, dashboardTarget, className = '', onBackgroundColorChange }) {
  const act = useAct()

  const questionId = field.questionId
  const question = useSel(s => s.dashboard.survey.questions.find(q => q.id === questionId))
  const answers = question.answers
  const value = dashboardTarget[field.id]
  const [internalValue, setInternalValue] = useState(value);
  const QuestionType = questionTypeComponents[question.questionType] || questionTypeComponents['unknown']

  if (question.questionType === 'checkbox') {
    className += ' center'
  }

  useEffect(() => {
    setInternalValue(value); // ensure always responds to state change
  }, [value])

  useEffect(() => {
    let completesGoal = false;
    
    if (answers && internalValue) {
      const selectedAnswer = answers.find(answer => answer.id === internalValue);
      completesGoal = selectedAnswer && selectedAnswer.setsGoalId !== null;
    }

    if (completesGoal) {
      onBackgroundColorChange('gold-background');
    } else if (field.config?.colorCodeBoolean) {
      const backgroundColor = internalValue === 'y' ? 'green-background' : 'red-background';
      onBackgroundColorChange(backgroundColor);
    } else {
      onBackgroundColorChange('');
    }
  }, [internalValue, field?.config?.colorCodeBoolean, onBackgroundColorChange, answers])

  const onChange = useMemo((answer) => {
    const commitUpdateFn = (answer) => act.app.answerQuestion({ dashboardTargetId: dashboardTarget.id, questionId, answer });
    const debounceDelay = question.questionType === 'text' ? 500 : 0
    const dCommitUpdateFn = _.debounce(commitUpdateFn, debounceDelay);

    return (answer) => {
      setInternalValue(answer);
      act.app.setPendingUpdate(dashboardTarget.id)
      dCommitUpdateFn(answer);
    };
  }, [dashboardTarget.id, questionId]);

  return (
    <QuestionType
      name={field.name}
      type={question.questionType}
      label={''}
      answers={answers}
      response={internalValue}
      setResponse={onChange}
      settings={{}}
      placeholder={null}
      className={className}
    />
  )
}
